import i18n from "../i18n";
import { getListDateLocale } from "./dateLocale";

const defaultT = (...args) => i18n.t(...args);

export function isVoucherExpired(voucher) {
  const expiry = getVoucherExpiryDate(voucher);
  return voucher.status === "expired" || Boolean(expiry && Date.now() >= expiry.getTime());
}

export function getVoucherExpiryDate(voucher) {
  for (const value of [voucher.expiryEndDatetime, voucher.validUntil, voucher.expiresAt]) {
    if (!value) continue;
    const date = new Date(value);
    if (Number.isFinite(date.getTime())) return date;
  }
  return null;
}

// Pass the component's `t` so the text re-renders on a language change.
export function formatVoucherExpiry(expiryDate, t = defaultT) {
  if (!expiryDate) {
    return { text: t("rewards.expiry.none"), className: "" };
  }

  const now = new Date();
  const expiry = new Date(expiryDate);
  if (!Number.isFinite(expiry.getTime())) {
    return { text: t("rewards.expiry.none"), className: "" };
  }
  const timeDiff = expiry - now;
  const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  if (timeDiff > 0 && timeDiff <= 24 * 60 * 60 * 1000) {
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    return {
      text: t("rewards.expiry.inHoursMinutes", { hours, minutes }),
      className: "expires-critical",
    };
  }

  if (timeDiff > 0 && daysRemaining <= 2) {
    return {
      text: t("rewards.expiry.inDays", { count: daysRemaining }),
      className: "expires-soon",
    };
  }

  if (timeDiff > 0) {
    return {
      text: t("rewards.expiry.on", {
        date: expiry.toLocaleDateString(getListDateLocale(), {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
      }),
      className: "",
    };
  }

  return {
    text: t("rewards.expired"),
    className: "expired",
  };
}

export function getVoucherReceivedDate(voucher) {
  if (!voucher) {
    return null;
  }

  const candidateFields = [
    voucher.rewardCreatedAt,
    voucher.receivedAt,
    voucher.redeemedAt,
    voucher.createdAt,
    voucher.updatedAt,
    voucher.claimedAt,
    voucher.issuedAt,
  ];

  for (const field of candidateFields) {
    if (!field) {
      continue;
    }

    const parsed = new Date(field);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
}

export function formatVoucherReceivedDate(dateInput) {
  if (!dateInput) {
    return null;
  }

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString(getListDateLocale(), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatWinnerNotification(winner) {
  if (!winner) {
    return null;
  }

  return {
    username: winner.username,
    rewardText: winner.reward || winner.rewardText || "a reward!",
  };
}

export function getRewardNotificationText(reward) {
  if (!reward) {
    return "50 tokens!";
  }

  if (reward.type === "token") {
    const tokenName = reward.source || reward.tokenType || "tokens";
    return `${reward.amount} ${tokenName}!`;
  }

  if (reward.type === "voucher") {
    if (reward.amount && reward.amount > 0) {
      return `a $${reward.amount} voucher!`;
    }
    return `a ${reward.source || `${reward.amount}% Off`} voucher!`;
  }

  return "50 tokens!";
}
